/**
 * App lock (ADR-0021): a PIN set on first use, required again after
 * `appLockMinutes` without interaction. The PIN never leaves the device and
 * is stored only as a PBKDF2 hash.
 *
 * Native biometric unlock (Face ID / fingerprint) plugs in at
 * `unlockWithBiometrics` once a biometric Capacitor plugin is installed; the
 * PIN remains the fallback.
 */

const KEY = 'sss.appLock';
const FAILS_KEY = 'sss.appLock.fails';
export const MAX_PIN_FAILURES = 10;
const ITERATIONS = 150_000;

interface StoredPin {
  salt: string;
  hash: string;
  iterations: number;
}

const enc = new TextEncoder();
const hex = (b: ArrayBuffer) => Array.from(new Uint8Array(b), (x) => x.toString(16).padStart(2, '0')).join('');
const unhex = (s: string) => new Uint8Array(s.match(/../g)!.map((h) => parseInt(h, 16)));

async function derive(pin: string, salt: Uint8Array, iterations: number): Promise<string> {
  const key = await crypto.subtle.importKey('raw', enc.encode(pin), 'PBKDF2', false, ['deriveBits']);
  const bits = await crypto.subtle.deriveBits({ name: 'PBKDF2', hash: 'SHA-256', salt: salt as BufferSource, iterations }, key, 256);
  return hex(bits);
}

export function isValidPin(pin: string): boolean {
  return /^\d{4,8}$/.test(pin) && !/^(\d)\1+$/.test(pin) && !'0123456789'.includes(pin) && !'9876543210'.includes(pin);
}

export function hasPin(): boolean {
  try {
    return !!localStorage.getItem(KEY);
  } catch {
    return false;
  }
}

export async function setPin(pin: string): Promise<void> {
  if (!isValidPin(pin)) throw new Error('INVALID_PIN');
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const stored: StoredPin = { salt: hex(salt.buffer), hash: await derive(pin, salt, ITERATIONS), iterations: ITERATIONS };
  localStorage.setItem(KEY, JSON.stringify(stored));
  localStorage.removeItem(FAILS_KEY);
}

export async function verifyPin(pin: string): Promise<{ ok: boolean; failures: number }> {
  const raw = localStorage.getItem(KEY);
  if (!raw) return { ok: true, failures: 0 };
  const s = JSON.parse(raw) as StoredPin;
  const ok = (await derive(pin, unhex(s.salt), s.iterations)) === s.hash;
  const failures = ok ? 0 : Number(localStorage.getItem(FAILS_KEY) ?? 0) + 1;
  if (ok) localStorage.removeItem(FAILS_KEY);
  else localStorage.setItem(FAILS_KEY, String(failures));
  return { ok, failures };
}

export function clearPin(): void {
  localStorage.removeItem(KEY);
  localStorage.removeItem(FAILS_KEY);
}

/** Placeholder for native biometric unlock; returns false until a plugin is wired in. */
export async function unlockWithBiometrics(): Promise<boolean> {
  return false;
}

/** Tracks user activity and reports when the app should lock. */
export class IdleTracker {
  private last: number;
  constructor(
    private readonly lockAfterMs: number,
    private readonly now: () => number = () => Date.now(),
  ) {
    this.last = now();
  }
  touch(): void {
    this.last = this.now();
  }
  shouldLock(): boolean {
    return this.now() - this.last >= this.lockAfterMs;
  }
}
